import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type UseQuerySearchStateOptions<T> = {
  /** Convert state -> string for the URL. Default: String(value). */
  serialize?: (value: T) => string
  /** Convert string from the URL -> state. Default: (s) as unknown as T. */
  deserialize?: (raw: string | null) => T
  /** Use history.replaceState (default) or pushState when writing. */
  push?: boolean
  /**
   * When the state equals the initialValue, remove the param for cleaner URLs.
   * Default: true (remove).
   */
  removeIfInitial?: boolean
  /** Optional equality check for deciding whether to remove. Default: Object.is */
  isEqual?: (a: T, b: T) => boolean
}

/**
 * useQuerySearchState(name, initialValue, options?)
 *
 * Example:
 * const [q, setQ] = useQuerySearchState("q", "", {
 *   serialize: (s) => s.trim(),
 *   deserialize: (s) => s ?? "",
 *   push: false
 * });
 */
export function useQuerySearchState<T>(
  name: string,
  initialValue: T,
  options: UseQuerySearchStateOptions<T> = {}
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const {
    serialize = (v: T) => String(v),
    deserialize = (raw: string | null) => (raw as unknown) as T,
    push = false,
    removeIfInitial = true,
    isEqual = Object.is
  } = options

  const isBrowser = typeof window !== 'undefined'

  // Resolve initial state: URL value (if present) takes precedence.
  const getURLValue = useCallback((): T => {
    if (!isBrowser) return initialValue
    const sp = new URLSearchParams(window.location.search)
    const raw = sp.get(name)
    if (raw === null) return initialValue
    try {
      return deserialize(raw)
    } catch {
      // If parsing fails, fallback to initialValue
      return initialValue
    }
  }, [deserialize, initialValue, isBrowser, name])

  const [value, setValue] = useState<T>(() => getURLValue())

  // Keep a ref to the latest value to avoid stale closures in listeners
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // Sync URL when value changes
  useEffect(() => {
    if (!isBrowser) return

    const url = new URL(window.location.href)
    const sp = url.searchParams

    // Decide whether to keep/remove the param
    if (removeIfInitial && isEqual(value, initialValue)) {
      sp.delete(name)
    } else {
      sp.set(name, serialize(value))
    }

    // Only update if something actually changed
    const nextSearch = sp.toString()
    const currSearch = window.location.search.startsWith('?') ? window.location.search.slice(1) : window.location.search

    if (nextSearch !== currSearch) {
      const nextURL = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`
      if (push) {
        window.history.pushState(null, '', nextURL)
      } else {
        window.history.replaceState(null, '', nextURL)
      }
    }
  }, [isBrowser, name, value, serialize, push, removeIfInitial, initialValue, isEqual])

  // Update local state if the user navigates (back/forward) and the URL changes
  useEffect(() => {
    if (!isBrowser) return undefined

    const onPopState = () => {
      setValue(getURLValue())
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [getURLValue, isBrowser])

  // Provide a stable setter that mirrors React's setState API (value or updater fn)
  const setQueryValue = useCallback<React.Dispatch<React.SetStateAction<T>>>(next => {
    setValue(prev => (typeof next === 'function' ? (next as any)(prev) : next))
  }, [])

  return [value, setQueryValue]
}
